import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid2 as Grid,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Chip,
  Paper,
} from "@mui/material";
import {
  Save,
  NotificationsActive,
  Security,
  AccountBalance,
  Edit,
  Add,
  Delete,
  Link as LinkIcon,
  Info,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  loadPpSettings,
  savePpSettings,
} from "../../../../store/actions/ppActions";

const PpSettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const settings = ppState?.settings?.data || {};
  const loading = ppState?.settings?.loading || false;

  const [cityDialogOpen, setCityDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load settings on mount
  useEffect(() => {
    dispatch(loadPpSettings());
  }, [dispatch]);

  // Form validation schema
  const validationSchema = Yup.object({
    payoutShare: Yup.number()
      .required("Payout share is required")
      .min(1, "Minimum 1%")
      .max(100, "Maximum 100%"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Notarization settings
      providesOfficeNotarization:
        settings.notarization?.providesOfficeNotarization || false,
      ronEnabled: settings.notarization?.ronEnabled || false,
      ipenEnabled: settings.notarization?.ipenEnabled || false,
      ipenDeviceMode: settings.notarization?.ipenDeviceMode || "same_device",

      // Payout settings
      payoutShare: settings.payoutShare || 30,

      // City profiles
      cityProfiles: settings.cityProfiles || [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        const settingsData = {
          notarization: {
            providesOfficeNotarization: values.providesOfficeNotarization,
            ronEnabled: values.ronEnabled,
            ipenEnabled: values.ipenEnabled,
            ipenDeviceMode: values.ipenDeviceMode,
          },
          payoutShare: values.payoutShare,
          cityProfiles: values.cityProfiles,
        };

        await dispatch(savePpSettings(settingsData));
        // Show success toast here if you have a toast system
      } catch (error) {
        console.error("Failed to save settings:", error);
        // Show error toast here
      } finally {
        setSaving(false);
      }
    },
  });

  const handleCityProfileSave = (cityData) => {
    const currentProfiles = [...formik.values.cityProfiles];

    if (editingCity) {
      // Update existing
      const index = currentProfiles.findIndex((c) => c.id === editingCity.id);
      if (index !== -1) {
        currentProfiles[index] = { ...editingCity, ...cityData };
      }
    } else {
      // Add new
      const newCity = {
        id: Date.now().toString(),
        ...cityData,
      };
      currentProfiles.push(newCity);
    }

    formik.setFieldValue("cityProfiles", currentProfiles);
    setCityDialogOpen(false);
    setEditingCity(null);
  };

  const handleDeleteCity = (cityId) => {
    const updatedProfiles = formik.values.cityProfiles.filter(
      (c) => c.id !== cityId
    );
    formik.setFieldValue("cityProfiles", updatedProfiles);
  };

  const CityProfileDialog = () => {
    const [cityFormData, setCityFormData] = useState({
      name: editingCity?.name || "",
      portalLink: editingCity?.portalLink || "",
      autoReport: editingCity?.autoReport || false,
      requiredFields: editingCity?.requiredFields || [],
    });

    const availableFields = [
      "permit_number",
      "address",
      "contractor_license",
      "property_owner",
      "job_value",
      "square_footage",
      "lot_number",
      "subdivision",
    ];

    const handleFieldToggle = (field) => {
      const currentFields = cityFormData.requiredFields;
      const newFields = currentFields.includes(field)
        ? currentFields.filter((f) => f !== field)
        : [...currentFields, field];
      setCityFormData((prev) => ({ ...prev, requiredFields: newFields }));
    };

    return (
      <Dialog
        open={cityDialogOpen}
        onClose={() => setCityDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCity ? "Edit City Profile" : "Add City Profile"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="City/County Name"
                value={cityFormData.name}
                onChange={(e) =>
                  setCityFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Portal Link"
                value={cityFormData.portalLink}
                onChange={(e) =>
                  setCityFormData((prev) => ({
                    ...prev,
                    portalLink: e.target.value,
                  }))
                }
                placeholder="https://city.gov/permits"
                helperText="Link to the city's permit portal"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={cityFormData.autoReport}
                    onChange={(e) =>
                      setCityFormData((prev) => ({
                        ...prev,
                        autoReport: e.target.checked,
                      }))
                    }
                  />
                }
                label="Auto-report when permit becomes Active"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Required Fields
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={1}>
                  {availableFields.map((field) => (
                    <Grid key={field} size={{ xs: 6, sm: 4 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={cityFormData.requiredFields.includes(
                              field
                            )}
                            onChange={() => handleFieldToggle(field)}
                          />
                        }
                        label={field.replace("_", " ")}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleCityProfileSave(cityFormData)}
            disabled={!cityFormData.name}
          >
            {editingCity ? "Update" : "Add"} City Profile
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h4" sx={{ mb: 3 }}>
        Private Provider Settings
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        {/* Notarization Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Security sx={{ mr: 1 }} />
              <Typography variant="h6">Notarization Services</Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="providesOfficeNotarization"
                      checked={formik.values.providesOfficeNotarization}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Provides office notarization services"
                />
              </Grid>

              {formik.values.providesOfficeNotarization && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="ronEnabled"
                          checked={formik.values.ronEnabled}
                          onChange={formik.handleChange}
                        />
                      }
                      label="Remote Online Notarization (RON)"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="ipenEnabled"
                          checked={formik.values.ipenEnabled}
                          onChange={formik.handleChange}
                        />
                      }
                      label="In-Person Electronic Notarization (IPEN)"
                    />
                  </Grid>

                  {formik.values.ipenEnabled && (
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth>
                        <InputLabel>IPEN Device Mode</InputLabel>
                        <Select
                          name="ipenDeviceMode"
                          value={formik.values.ipenDeviceMode}
                          label="IPEN Device Mode"
                          onChange={formik.handleChange}
                        >
                          <MenuItem value="same_device">
                            Same Device (PP and contractor)
                          </MenuItem>
                          <MenuItem value="different_devices">
                            Different Devices
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <strong>Same Device:</strong> No notifications needed -
                        PP notarizes and contractor signs on the same device.
                        <br />
                        <strong>Different Devices:</strong> System sends
                        notifications to each signer to complete the process.
                      </Alert>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Payout Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <AccountBalance sx={{ mr: 1 }} />
              <Typography variant="h6">Payout Configuration</Typography>
            </Box>

            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, sm: 8 }}>
                <Typography variant="body1" gutterBottom>
                  Commission Percentage
                </Typography>
                <Slider
                  value={formik.values.payoutShare}
                  onChange={(e, newValue) =>
                    formik.setFieldValue("payoutShare", newValue)
                  }
                  aria-labelledby="payout-share-slider"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                  step={1}
                  marks={[
                    { value: 10, label: "10%" },
                    { value: 30, label: "30%" },
                    { value: 50, label: "50%" },
                  ]}
                  min={1}
                  max={50}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Share Percentage"
                  name="payoutShare"
                  type="number"
                  value={formik.values.payoutShare}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.payoutShare &&
                    Boolean(formik.errors.payoutShare)
                  }
                  helperText={
                    formik.touched.payoutShare && formik.errors.payoutShare
                  }
                  InputProps={{ endAdornment: "%" }}
                  inputProps={{ min: 1, max: 50 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  Your current commission rate is{" "}
                  <strong>{formik.values.payoutShare}%</strong> of total revenue
                  collected. This applies to all plan review and inspection
                  services.
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* City Profiles */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LinkIcon sx={{ mr: 1 }} />
                <Typography variant="h6">City Profiles</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setEditingCity(null);
                  setCityDialogOpen(true);
                }}
              >
                Add City
              </Button>
            </Box>

            {formik.values.cityProfiles.length === 0 ? (
              <Alert severity="info">
                No city profiles configured. Add city profiles to enable
                automatic reporting and define required fields.
              </Alert>
            ) : (
              <List>
                {formik.values.cityProfiles.map((city) => (
                  <ListItem key={city.id} divider>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {city.name}
                          <Chip
                            size="small"
                            icon={
                              city.autoReport ? <CheckCircle /> : <Warning />
                            }
                            label={city.autoReport ? "Auto Report" : "Manual"}
                            color={city.autoReport ? "success" : "warning"}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Portal: {city.portalLink || "Not configured"}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Required Fields: {city.requiredFields?.length || 0}{" "}
                            configured
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => {
                          setEditingCity(city);
                          setCityDialogOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteCity(city.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Advanced Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={() => navigate("/pp/settings/city-reporting")}
                >
                  City Reporting Interfaces
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Security />}
                  onClick={() => navigate("/pp/settings/digital-signature")}
                >
                  Digital Signature Setup
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Alert severity="info" sx={{ flexGrow: 1, mr: 2 }}>
                <Info sx={{ mr: 1 }} />
                Changes will be applied immediately and affect all future
                transactions.
              </Alert>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<Save />}
                disabled={loading || saving}
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>

      <CityProfileDialog />
    </Box>
  );
};

export default PpSettingsPage;
